import React, { useEffect, useMemo, useRef, useState } from "react"
import moment from "moment"
import firebase from "firebase"
import { Sparkles } from "lucide-react"

import { db } from "../../../utils/firebase"
import Window from "../../../components/structure/window/window"
import Badge from "../../../components/ui/badge/badge"
import Button from "../../../components/ui/button/button"
import { LoadingIcon } from "../../../utils/svgs"
import { getCommunicationBodyUrl, loadCommunicationBody } from "../../../utils/communications/load-body"

import AIToolsPanel from "./ai-tools/panel"
import "./ai-tools/panel.scss"

import ClassificationBadge from "./classification-badge"

/**
 * Modal-style drawer that shows everything we know about a single ingested
 * communication, including the full HTML body rendered in a sandboxed iframe.
 *
 * Sandbox philosophy: the iframe runs with `sandbox` and **no allow flags**.
 * That blocks scripts, forms, top-level navigation, and same-origin access —
 * effectively a read-only render of arbitrary email HTML. External `<img>`
 * tags will still load (we're not blocking image-src), which means trackers
 * fire when an admin opens the drawer. That's a deliberate v1 trade-off; if
 * tracker-blocking becomes important we can prepend a `<meta http-equiv=
 * "Content-Security-Policy">` to the srcdoc to lock down the document
 * entirely.
 */
function EmailDrawer({ comm, close }) {
	const [body, setBody] = useState(null)
	const [bodyError, setBodyError] = useState(null)
	const [loading, setLoading] = useState(true)
	const [inlineImageUrls, setInlineImageUrls] = useState({})
	const iframeRef = useRef(null)

	useEffect(() => {
		let cancelled = false

		const load = async () => {
			if (!comm?.body_storage_path) {
				setBody("")
				setLoading(false)
				return
			}

			try {
				const html = await loadCommunicationBody(comm.body_storage_path)
				if (cancelled) return
				setBody(html || "")
				setLoading(false)
			} catch (err) {
				if (cancelled) return
				console.error("Failed to load communication body", err)
				setBodyError(err?.message || "Failed to load body")
				setLoading(false)
			}
		}

		load()

		return () => {
			cancelled = true
		}
	}, [comm?.body_storage_path])

	useEffect(() => {
		let cancelled = false

		const loadInlineImages = async () => {
			const inlineImages = (comm?.attachments || []).filter(
				(attachment) => attachment.is_inline && attachment.storage_path
			)

			if (inlineImages.length === 0) {
				setInlineImageUrls({})
				return
			}

			const next = {}
			await Promise.all(
				inlineImages.map(async (attachment) => {
					try {
						const url = attachment.download_url || await getCommunicationBodyUrl(attachment.storage_path)
						for (const key of inlineImageLookupKeys(attachment)) {
							next[key] = url
						}
					} catch (err) {
						console.error("Failed to load inline image URL", err)
					}
				})
			)

			if (!cancelled) setInlineImageUrls(next)
		}

		loadInlineImages()

		return () => {
			cancelled = true
		}
	}, [comm?.attachments])

	const renderedBody = useMemo(
		() => replaceCidImageSources(body, inlineImageUrls),
		[body, inlineImageUrls]
	)

	if (!comm) return null

	const occurredAt = comm.occurred_at?.seconds
		? moment.unix(comm.occurred_at.seconds).format("MMM D[,] YYYY [at] HH:mm")
		: null

	const recipientText = (recipients = []) =>
		recipients
			.map((r) => (r.name ? `${r.name} <${r.address}>` : r.address))
			.join(", ")

	return (
		<Window
			title={comm.subject || "(no subject)"}
			className="email-drawer"
			close={() => close()}>
			<div className="email-drawer-body">
				{/* Metadata grid */}
				<div className="email-drawer-meta">
					<MetaRow
						label="From"
						value={
							comm.from?.name ? `${comm.from.name} <${comm.from.address}>` : comm.from?.address
						}
					/>
					<MetaRow
						label="To"
						value={recipientText(comm.to) || <small className="no-value">—</small>}
					/>
					{comm.cc?.length > 0 && (
						<MetaRow
							label="CC"
							value={recipientText(comm.cc)}
						/>
					)}
					<MetaRow
						label="Received"
						value={occurredAt || <small className="no-value">—</small>}
					/>
					<MetaRow
						label="Classification"
						value={
							<>
								<ClassificationBadge comm={comm} />
								{comm.tier === "RESTRICTED" && (
									<Badge
										type="NEGATIVE"
										label="Restricted"
									/>
								)}
								{typeof comm.classification_confidence === "number" && (
									<small className="email-drawer-confidence">
										{Math.round(comm.classification_confidence * 100)}% confidence
									</small>
								)}
							</>
						}
					/>
					{comm.classification_reasoning && (
						<MetaRow
							label="Reasoning"
							value={
								<span className="email-drawer-reasoning">
									{comm.classification_reasoning}
								</span>
							}
						/>
					)}
					{comm.classification_visual_evidence_reasoning && (
						<MetaRow
							label="Visual evidence"
							value={
								<span className="email-drawer-reasoning">
									{comm.classification_visual_evidence_reasoning}
								</span>
							}
						/>
					)}
					{comm.classification_input_stats && (
						<MetaRow
							label="Classifier input"
							value={<ClassifierInputStats stats={comm.classification_input_stats} />}
						/>
					)}
					{comm.classification === "enquiry.new" && (
						<MetaRow
							label=""
							value={<SendToEnquiryIngestButton comm={comm} />}
						/>
					)}
					<ReferencesRow references={comm.classification_references} />
					{comm.also_received_by?.length > 0 && (
						<MetaRow
							label="Also received by"
							value={`${comm.also_received_by.length} other monitored mailbox${
								comm.also_received_by.length === 1 ? "" : "es"
							}`}
						/>
					)}
					{comm.attachments?.some((attachment) => !attachment.is_inline) && (
						<MetaRow
							label="Attachments"
							value={<AttachmentsList attachments={comm.attachments} />}
						/>
					)}
				</div>

				{/* Internet identifiers — small text, useful for debugging and de-dup tracing */}
				<div className="email-drawer-ids">
					<small>
						<strong>Communication ID:</strong> {comm.id}
					</small>
					<small>
						<strong>Message ID:</strong> {comm.internet_message_id}
					</small>
					<small>
						<strong>Graph ID:</strong> {comm.graph_message_id}
					</small>
					<small>
						<strong>Storage prefix:</strong> communications/{comm.id}/
					</small>
				</div>

				{/* AI tooling — collapsible developer panel for running configurable
				    OpenAI tools (classifier, etc.) against this email. */}
				<AIToolsPanel comm={comm} />

				{/* Body preview */}
				<div className="email-drawer-body-section">
					<h3>Body</h3>

					{loading && (
						<div className="email-drawer-body-loading">
							<div className="svg-loading-wrap">
								<LoadingIcon />
							</div>
							<small>Loading email body...</small>
						</div>
					)}

					{!loading && bodyError && (
						<div className="email-drawer-body-error">
							<p>{bodyError}</p>
							<small>
								Check that you have <code>is_admin</code> set on your user doc and that
								Firebase Storage rules have been deployed.
							</small>
						</div>
					)}

					{!loading && !bodyError && !body && (
						<div className="email-drawer-body-empty">
							<small>This email has no body content.</small>
						</div>
					)}

					{!loading && !bodyError && body && (
						<iframe
							ref={iframeRef}
							className="email-drawer-iframe"
							sandbox=""
							srcDoc={renderedBody}
							title={comm.subject || "Email body"}
						/>
					)}
				</div>
			</div>
		</Window>
	)
}

function MetaRow({ label, value }) {
	return (
		<div className="email-drawer-meta-row">
			<span className="email-drawer-meta-label">{label}</span>
			<span className="email-drawer-meta-value">{value}</span>
		</div>
	)
}

/**
 * Renders the booking / quote / enquiry references the classifier extracted,
 * grouped by type. Skipped entirely when nothing was found in any group.
 */
function ReferencesRow({ references }) {
	if (!references) return null
	const { booking = [], quote = [], enquiry = [] } = references
	if (!booking.length && !quote.length && !enquiry.length) return null

	return (
		<div className="email-drawer-meta-row email-drawer-references">
			<span className="email-drawer-meta-label">References</span>
			<span className="email-drawer-meta-value">
				{booking.length > 0 && (
					<ReferenceGroup
						label="Booking"
						items={booking}
					/>
				)}
				{quote.length > 0 && (
					<ReferenceGroup
						label="Quote"
						items={quote}
					/>
				)}
				{enquiry.length > 0 && (
					<ReferenceGroup
						label="Enquiry"
						items={enquiry}
					/>
				)}
			</span>
		</div>
	)
}

function ReferenceGroup({ label, items }) {
	return (
		<span className="email-drawer-reference-group">
			<small className="email-drawer-reference-label">{label}:</small>
			{items.map((item) => (
				<code
					key={item}
					className="email-drawer-reference-item">
					{item}
				</code>
			))}
		</span>
	)
}

function ClassifierInputStats({ stats }) {
	const parts = []
	if (typeof stats.external_images_attached === "number") {
		parts.push(`${stats.external_images_attached} external image${stats.external_images_attached === 1 ? "" : "s"}`)
	}
	if (typeof stats.inline_images_attached === "number") {
		parts.push(`${stats.inline_images_attached} inline image${stats.inline_images_attached === 1 ? "" : "s"}`)
	}
	if (typeof stats.attachment_images_attached === "number") {
		parts.push(`${stats.attachment_images_attached} attachment image${stats.attachment_images_attached === 1 ? "" : "s"}`)
	}
	if (typeof stats.files_attached === "number") {
		parts.push(`${stats.files_attached} file${stats.files_attached === 1 ? "" : "s"}`)
	}

	if (parts.length === 0) return <small className="no-value">No attachment diagnostics</small>
	return <small className="email-drawer-classifier-input">{parts.join(", ")}</small>
}

/**
 * Manual forwarder for the Enquiry Ingest feedback queue. Writes a denormalised
 * doc into `enquiry_queue` keyed by the source comm's id — making it idempotent,
 * so re-clicking the button just shows the "already in queue" state rather than
 * creating duplicates.
 *
 * Lives in the email drawer (not the row) so the user can review the email
 * + classification before adding it to the tuning set.
 */
function SendToEnquiryIngestButton({ comm }) {
	// Tracks whether a doc with the same id as this comm already exists in the
	// queue. Single read on mount — the button doesn't need a live subscription.
	const [alreadyQueued, setAlreadyQueued] = useState(null)
	const [sending, setSending] = useState(false)
	const [error, setError] = useState(null)

	useEffect(() => {
		let cancelled = false
		db.collection("enquiry_queue")
			.doc(comm.id)
			.get()
			.then((snap) => {
				if (!cancelled) setAlreadyQueued(snap.exists)
			})
			.catch(() => {
				if (!cancelled) setAlreadyQueued(false)
			})
		return () => {
			cancelled = true
		}
	}, [comm.id])

	const send = async () => {
		setSending(true)
		setError(null)
		try {
			await db
				.collection("enquiry_queue")
				.doc(comm.id)
				.set(
					{
						comm_id: comm.id,
						subject: comm.subject || "",
						from_name: comm.from?.name || "",
						from_address: comm.from?.address || "",
						received_at: comm.occurred_at || null,
						forwarded_at: firebase.firestore.FieldValue.serverTimestamp(),
						forwarded_from_mailbox_id: comm.mailbox_id || null,
						source_classification: comm.classification || null,
						source_classification_confidence:
							typeof comm.classification_confidence === "number"
								? comm.classification_confidence
								: null,
					},
					{ merge: true }
				)
			setAlreadyQueued(true)
		} catch (err) {
			setError(err?.message || "Failed to send to enquiry ingest")
		} finally {
			setSending(false)
		}
	}

	return (
		<div className="email-drawer-enquiry-forward">
			<Button
				small={true}
				icon={<Sparkles size={12} />}
				label={alreadyQueued ? "Already in enquiry ingest" : "Send to enquiry ingest"}
				loading={sending}
				loadingText="Sending..."
				disabled={sending || alreadyQueued === true}
				onClick={send}
			/>
			{error && <small className="email-drawer-enquiry-forward-error">{error}</small>}
		</div>
	)
}

function AttachmentsList({ attachments = [] }) {
	const files = useMemo(
		() => attachments.filter((attachment) => !attachment.is_inline),
		[attachments]
	)
	const [urls, setUrls] = useState({})
	const [failedUrls, setFailedUrls] = useState({})

	useEffect(() => {
		let cancelled = false

		const loadUrls = async () => {
			const nextUrls = {}
			const nextFailed = {}

			await Promise.all(
				files.map(async (attachment) => {
					if (!attachment.storage_path) return

					try {
						nextUrls[attachment.storage_path] =
							attachment.download_url || await getCommunicationBodyUrl(attachment.storage_path)
					} catch (err) {
						console.error("Failed to load attachment URL", err)
						nextFailed[attachment.storage_path] = true
					}
				})
			)

			if (cancelled) return
			setUrls(nextUrls)
			setFailedUrls(nextFailed)
		}

		loadUrls()

		return () => {
			cancelled = true
		}
	}, [files])

	if (files.length === 0) return <small className="no-value">—</small>

	return (
		<span className="email-drawer-attachments">
			{files.map((attachment) => {
				const url = urls[attachment.storage_path]
				const isImage = attachment.content_type?.startsWith("image/")

				return (
					<span
						key={attachment.storage_path || attachment.graph_attachment_id || attachment.name}
						className={`email-drawer-attachment ${isImage ? "is-image" : ""}`}>
						{isImage && url && (
							<a
								href={url}
								target="_blank"
								rel="noreferrer"
								className="email-drawer-attachment-preview">
								<img
									src={url}
									alt={attachment.name || "Attachment preview"}
								/>
							</a>
						)}

						<span className="email-drawer-attachment-details">
							{url ? (
								<a
									href={url}
									target="_blank"
									rel="noreferrer">
									{attachment.name || "Attachment"}
								</a>
							) : (
								<strong>{attachment.name || "Attachment"}</strong>
							)}
							<small>
								{attachment.content_type || "unknown type"}
								{typeof attachment.size === "number" ? ` · ${formatBytes(attachment.size)}` : ""}
								{failedUrls[attachment.storage_path] ? " · preview unavailable" : ""}
							</small>
							{attachment.storage_path && (
								<code className="email-drawer-attachment-path">{attachment.storage_path}</code>
							)}
						</span>
					</span>
				)
			})}
		</span>
	)
}

function formatBytes(bytes) {
	if (!bytes || bytes <= 0) return "0 B"
	const units = ["B", "KB", "MB", "GB"]
	const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
	const value = bytes / Math.pow(1024, index)
	return `${value >= 10 || index === 0 ? Math.round(value) : value.toFixed(1)} ${units[index]}`
}

function replaceCidImageSources(html, inlineImageUrls) {
	if (!html || !inlineImageUrls || Object.keys(inlineImageUrls).length === 0) return html

	return html.replace(/(src\s*=\s*)(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi, (match, prefix, doubleQuoted, singleQuoted, unquoted) => {
		const rawValue = doubleQuoted ?? singleQuoted ?? unquoted ?? ""
		if (!rawValue.toLowerCase().startsWith("cid:")) return match

		const url = findInlineImageUrl(rawValue, inlineImageUrls)
		if (!url) return match
		const quote = doubleQuoted !== undefined ? `"` : singleQuoted !== undefined ? `'` : `"`
		return `${prefix}${quote}${escapeHtmlAttribute(url)}${quote}`
	})
}

function findInlineImageUrl(cidValue, inlineImageUrls) {
	const normalised = normaliseContentId(cidValue)
	if (inlineImageUrls[normalised]) return inlineImageUrls[normalised]

	const filenamePart = normalised.split("@")[0]
	if (inlineImageUrls[filenamePart]) return inlineImageUrls[filenamePart]

	return null
}

function inlineImageLookupKeys(attachment) {
	return [
		attachment.content_id,
		attachment.content_location,
		attachment.name,
		attachment.graph_attachment_id,
	]
		.map(normaliseContentId)
		.filter(Boolean)
}

function normaliseContentId(contentId) {
	if (!contentId) return ""
	let next = String(contentId)
		.trim()
		.replace(/^cid:/i, "")
		.replace(/^<|>$/g, "")
	try {
		next = decodeURIComponent(next)
	} catch {
		// Content IDs are often plain strings; malformed URI sequences can be
		// compared as-is.
	}
	return next.toLowerCase()
}

function escapeHtmlAttribute(value) {
	return String(value)
		.replace(/&/g, "&amp;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
}

export default EmailDrawer
